import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Chip } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { TableFilterTextFieldDefault } from './TableFilterTextFieldDefault'
import { TranslateFunction } from 'src/i18n/useMessageSource'
import { TableFilterPopup, TableFilterPopupWithClose } from 'src/ui-shared/table/TableFilterPopup'

interface Props<T extends Record<string, any>, K extends keyof T> {
  options: T[]
  filter: string
  title: string
  label: string
  labelFieldName: K
  valueFieldName: K
  getOptionLabel?: (option: T) => string
  tableSettings: any
  setTableSettings: (value: any) => void
  onItemsSelected?: (items: T[]) => void
  translate?: TranslateFunction
}

export const TableFilterMultipleChoiceAutocomplete = <T extends Record<string, any>>({
  options,
  filter,
  title,
  label,
  labelFieldName,
  valueFieldName,
  getOptionLabel,
  tableSettings,
  setTableSettings,
  onItemsSelected,
  translate,
}: Props<T, keyof T>): ReactElement => {
  const popupRef = useRef<TableFilterPopupWithClose | null>(null)
  const [timeoutState, setTimeoutState] = useState<ReturnType<typeof setTimeout> | null>(null)
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(tableSettings[filter] ?? [])

  const getOptionValue = (option: T): string => (option as any)[valueFieldName]

  const selectedOptions = options.filter((option) => selectedOptionIds.includes(getOptionValue(option)))

  useEffect(() => {
    setSelectedOptionIds(tableSettings[filter] || [])
  }, [tableSettings])

  const handleSelectedOptions = (_: React.SyntheticEvent, newOptions: T[]) => {
    const transformedValues = newOptions.map((option) => getOptionValue(option))
    setSelectedOptionIds(transformedValues)

    if (timeoutState) {
      clearTimeout(timeoutState)
    }

    setTimeoutState(
      setTimeout(() => {
        setTableSettings({
          ...tableSettings,
          [filter]: transformedValues,
          page: 0,
        })

        if (onItemsSelected) {
          onItemsSelected(newOptions)
        }
      }, 300),
    )
  }

  const getOptionLabelInternal = (option: T): string => {
    if (getOptionLabel) {
      return getOptionLabel(option)
    } else {
      const label = (option as any)[labelFieldName]
      if (translate) {
        return translate(label)
      }
      return label
    }
  }

  return (
    <TableFilterPopup ref={popupRef} title={title} active={selectedOptionIds.length > 0}>
      <Autocomplete
        openOnFocus={true}
        options={options}
        value={selectedOptions}
        multiple
        getOptionLabel={getOptionLabelInternal}
        style={{ width: 400 }}
        onChange={handleSelectedOptions}
        renderValue={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip variant="outlined" label={getOptionLabelInternal(option)} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => <TableFilterTextFieldDefault {...params} label={label} />}
      />
    </TableFilterPopup>
  )
}
